import { Injectable } from '@angular/core';
import { effectiveShippedQty, round2 } from '../item-details/item-utils';

export interface MarginValidationResult {
  isValid: boolean;
  actualMarginPercent: number;
  minimumMarginPercent: number;
  lineCost: number;
  sellingAmount: number;
  error?: string;
}

export interface OrderContributionMarginResult {
  totalOrderCost: number;
  totalSellingAmount: number;
  actualOrderMarginPercent: number;
}

export interface LineMarginViolation {
  itemNo: string;
  itemName: string;
  actual: number;
  required: number;
  lineIndex?: number;
}

export interface OrderMarginViolation {
  isValid: boolean;
  actual: number;
  required: number;
  totalOrderCost: number;
  totalSellingAmount: number;
}

export interface MarginApprovalSummary {
  failingLines: LineMarginViolation[];
  orderViolation: OrderMarginViolation | null;
  hasAnyViolation: boolean;
}

@Injectable({ providedIn: 'root' })
export class MarginValidationService {
  /** Unit cost from line (MCCSU1 / cost_price). */
  resolveUnitCost(line: any): number {
    return Number(line?.cost ?? line?.costPrice) || 0;
  }

  /** Line cost = unit cost × effective shipped qty (COALESCE(shipQty, ordered)). */
  resolveLineCost(line: any): number {
    return round2(this.resolveUnitCost(line) * effectiveShippedQty(line));
  }

  /** Net selling amount for margin — same base as item-level validation. */
  resolveLineSellingAmount(line: any): number {
    return Number(line?.amountAfterDiscount ?? line?.netAmount) || 0;
  }

  validateRowContributionMargin(row: any): MarginValidationResult | null {
    const costPrice = this.resolveUnitCost(row);
    const minimumMarginPercent = Number(row?.minimumMargin ?? row?.itemMcm) || 0;
    if (costPrice <= 0 && minimumMarginPercent <= 0) {
      return null;
    }
    const quantity = effectiveShippedQty(row);
    const sellingAmount = this.resolveLineSellingAmount(row);
    return this.calculateItemContributionMargin(costPrice, quantity, sellingAmount, minimumMarginPercent);
  }

  collectLineMarginViolations(itemList: any[]): LineMarginViolation[] {
    const violations: LineMarginViolation[] = [];
    (itemList || []).forEach((line, index) => {
      const result = this.validateRowContributionMargin(line);
      if (result && !result.isValid) {
        violations.push({
          itemNo: String(line?.itemNumber ?? line?.itemNo ?? '').trim(),
          itemName: String(line?.itemName ?? '').trim(),
          actual: result.actualMarginPercent,
          required: result.minimumMarginPercent,
          lineIndex: index
        });
      }
    });
    return violations;
  }

  collectOrderMarginViolation(itemList: any[], orderMinimumPercent: number): OrderMarginViolation {
    const orderMargin = this.calculateOrderContributionMargin(itemList);
    const required = Number(orderMinimumPercent) || 0;
    const actual = orderMargin.actualOrderMarginPercent;
    return {
      isValid: actual >= required,
      actual,
      required,
      totalOrderCost: orderMargin.totalOrderCost,
      totalSellingAmount: orderMargin.totalSellingAmount
    };
  }

  buildMarginApprovalSummary(itemList: any[], orderMinimumPercent: number): MarginApprovalSummary {
    const failingLines = this.collectLineMarginViolations(itemList);
    const orderViolation =
      orderMinimumPercent > 0 ? this.collectOrderMarginViolation(itemList, orderMinimumPercent) : null;
    const hasOrderViolation = orderViolation != null && !orderViolation.isValid;
    return {
      failingLines,
      orderViolation: hasOrderViolation ? orderViolation : null,
      hasAnyViolation: failingLines.length > 0 || hasOrderViolation
    };
  }

  /**
   * Item-level contribution margin: ((Selling Amount - Line Cost) / Selling Amount) × 100
   * Selling Amount = net amount after discount (ex VAT).
   */
  calculateItemContributionMargin(
    costPrice: number,
    quantity: number,
    sellingAmount: number,
    minimumMarginPercent: number
  ): MarginValidationResult {
    const lineCost = round2(costPrice * quantity);
    const actualMarginPercent =
      sellingAmount > 0 ? round2(((sellingAmount - lineCost) / sellingAmount) * 100) : 0;
    const isValid = actualMarginPercent >= minimumMarginPercent;

    return {
      isValid,
      actualMarginPercent,
      minimumMarginPercent,
      lineCost,
      sellingAmount,
      error: !isValid
        ? `Actual contribution margin ${actualMarginPercent.toFixed(2)}% is below minimum required ${minimumMarginPercent.toFixed(2)}%.`
        : undefined
    };
  }

  /**
   * Order-level contribution margin before Save Draft.
   * Total Order Cost = SUM(unit cost × qty); Total Selling = SUM(net amount after discount).
   */
  calculateOrderContributionMargin(itemList: any[]): OrderContributionMarginResult {
    let totalOrderCost = 0;
    let totalSellingAmount = 0;

    (itemList || []).forEach((line) => {
      const lineCost = this.resolveLineCost(line);
      const sellingAmount = this.resolveLineSellingAmount(line);
      totalOrderCost += lineCost;
      totalSellingAmount += sellingAmount;
    });

    const actualOrderMarginPercent =
      totalSellingAmount > 0
        ? round2(((totalSellingAmount - totalOrderCost) / totalSellingAmount) * 100)
        : 0;

    return {
      totalOrderCost: round2(totalOrderCost),
      totalSellingAmount: round2(totalSellingAmount),
      actualOrderMarginPercent
    };
  }
}
